import { FileText, ThumbsUp, MessageCircle, Share2, Image } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import { platformBadge } from './PancakeDashboardFilters';

function formatDate(ts: string): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

function truncate(text: string, maxLen = 120): string {
  if (!text) return '—';
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

export default function PancakePostsList({ isDark }: { isDark: boolean }) {
  const rawPosts = usePancakeStore(s => s.posts);
  const posts = {
    total: rawPosts?.total ?? 0,
    posts: rawPosts?.posts ?? [],
  };

  return (
    <div className="space-y-4">
      {/* Total count */}
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
          <FileText className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
        </div>
        <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          Tổng: <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{posts.total.toLocaleString('en-US')}</span> bài viết
        </span>
      </div>

      {/* Posts grid */}
      {posts.posts.length === 0 && (
        <div className={`rounded-lg border p-8 text-center ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-500' : 'bg-white border-gray-200 text-gray-400'}`}>
          Không có bài viết
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {posts.posts.map((post) => (
          <div
            key={post.id}
            className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}
          >
            <div className="flex">
              {/* Thumbnail */}
              {post.full_picture && (
                <div className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32">
                  <img
                    src={post.full_picture}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              {!post.full_picture && (
                <div className={`flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                  <Image className={`w-8 h-8 ${isDark ? 'text-slate-700' : 'text-gray-300'}`} />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 p-3 sm:p-4 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  {platformBadge(post._platform)}
                  <span className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{post._pageName}</span>
                </div>

                <p className={`text-sm mb-2 ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                  {truncate(post.message)}
                </p>

                <p className={`text-xs mb-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {formatDate(post.created_time)}
                </p>

                {/* Engagement row */}
                <div className="flex items-center gap-4">
                  <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <ThumbsUp className="w-3.5 h-3.5" />
                    {(post.likes ?? 0).toLocaleString('en-US')}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <MessageCircle className="w-3.5 h-3.5" />
                    {(post.comments ?? 0).toLocaleString('en-US')}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Share2 className="w-3.5 h-3.5" />
                    {(post.shares ?? 0).toLocaleString('en-US')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
